import React, { useState, useContext } from "react";
import {
  Card,
  Button,
  Typography,
  Alert,
  Space,
  Spin,
  Table,
  Divider,
  message,
} from "antd";
import {
  PlayCircleOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { AppContext } from "../context/AppContext";
import {
  migrateOpeningBalances,
  checkOpeningBalances,
} from "../utils/migrateOpeningBalances";

const { Title, Text, Paragraph } = Typography;

const AccountingMigration = () => {
  const { company } = useContext(AppContext);
  const token = localStorage.getItem("token");

  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [migrationStatus, setMigrationStatus] = useState(null);
  const [migrationResults, setMigrationResults] = useState(null);

  const handleCheckStatus = async () => {
    try {
      setChecking(true);
      const result = await checkOpeningBalances({ company, token });
      setMigrationStatus(result);
    } catch (error) {
      message.error("Failed to check migration status");
    } finally {
      setChecking(false);
    }
  };

  const handleRunMigration = async () => {
    try {
      setLoading(true);
      const result = await migrateOpeningBalances({ company, token });

      if (result.success) {
        setMigrationResults(result.results);
        message.success(result.message);
      } else {
        message.error(result.message || "Migration failed");
      }
    } catch (error) {
      message.error("Migration failed: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "Account",
      dataIndex: "account",
      key: "account",
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (amount) => `ZMW ${amount.toLocaleString()}`,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status, record) => {
        if (status === "success") {
          return (
            <Space>
              <CheckCircleOutlined style={{ color: "#52c41a" }} />
              <Text type="success">Success</Text>
            </Space>
          );
        } else {
          return (
            <Space>
              <WarningOutlined style={{ color: "#ff4d4f" }} />
              <Text type="danger">{record.error}</Text>
            </Space>
          );
        }
      },
    },
  ];

  return (
    <div style={{ padding: "24px" }}>
      <Card>
        <Title level={3}>
          <InfoCircleOutlined style={{ marginRight: 8 }} />
          Opening Balance Migration
        </Title>

        <Paragraph>
          This tool creates opening balance journal entries for your existing
          account balances. This is needed to initialize the double-entry
          accounting system.
        </Paragraph>

        <Alert
          message="Important: Run This Once Only"
          description="This migration creates journal entries for your current account balances. Only run this if your balance sheet is showing as out of balance."
          type="warning"
          showIcon
          style={{ marginBottom: 24 }}
        />

        <Space direction="vertical" style={{ width: "100%" }} size="large">
          {/* Check Status Section */}
          <Card size="small" title="Step 1: Check Current Status">
            <Space>
              <Button
                icon={<InfoCircleOutlined />}
                onClick={handleCheckStatus}
                loading={checking}
              >
                Check Migration Status
              </Button>

              {migrationStatus && (
                <Alert
                  message={migrationStatus.message}
                  type={migrationStatus.migrated ? "success" : "info"}
                  showIcon
                />
              )}
            </Space>
          </Card>

          {/* Run Migration Section */}
          <Card size="small" title="Step 2: Run Migration">
            <Space direction="vertical" style={{ width: "100%" }}>
              <Paragraph>
                This will create opening balance entries (Owner's Equity) for
                all your active accounts with positive balances.
              </Paragraph>

              <Button
                type="primary"
                icon={<PlayCircleOutlined />}
                onClick={handleRunMigration}
                loading={loading}
                size="large"
                disabled={migrationStatus?.migrated}
              >
                {loading
                  ? "Running Migration..."
                  : "Run Opening Balance Migration"}
              </Button>

              {migrationStatus?.migrated && (
                <Alert
                  message="Migration already completed"
                  description="Opening balance entries have already been created. Your balance sheet should now be balanced."
                  type="success"
                  showIcon
                />
              )}
            </Space>
          </Card>

          {/* Results Section */}
          {migrationResults && (
            <>
              <Divider />
              <Card
                size="small"
                title="Migration Results"
                extra={
                  <Space>
                    <Text type="success">
                      Success: {migrationResults.success}
                    </Text>
                    {migrationResults.failed > 0 && (
                      <Text type="danger">
                        Failed: {migrationResults.failed}
                      </Text>
                    )}
                  </Space>
                }
              >
                <Table
                  dataSource={migrationResults.details}
                  columns={columns}
                  pagination={false}
                  size="small"
                  rowKey="account"
                />
              </Card>

              <Alert
                message="Next Steps"
                description={
                  <div>
                    <Paragraph>
                      1. Go to <strong>Balance Sheet</strong> page and verify
                      it's now balanced
                    </Paragraph>
                    <Paragraph>
                      2. Check <strong>Trial Balance</strong> to see the opening
                      entries
                    </Paragraph>
                    <Paragraph style={{ marginBottom: 0 }}>
                      3. Review <strong>General Ledger</strong> for Cash and
                      Owner's Equity accounts
                    </Paragraph>
                  </div>
                }
                type="info"
                showIcon
              />
            </>
          )}
        </Space>
      </Card>
    </div>
  );
};

export default AccountingMigration;
